import { useState, useEffect } from 'react';
import Lottie from 'react-lottie';

import Typography from '~/shared/components/Typography';
import loadingSpinner from '~/shared/assets/lotties/loading-spinner.json';
import WarningIcon from '~/shared/assets/icons/warning-2.svg';
import InfoIcon from '~/shared/assets/icons/info-circle.svg';
import Exception from '~/shared/assets/icons/exception-2.svg';
import { Volume } from '~/shared/types/api';
import OptimisticSwitch from '~/fleet-manager/components/common/OptimisticSwitch';
// import ExceptionIcon from '~/shared/assets/icons/caution-red.svg';

import { isVolumeExcludedFromAutodatafy, formatNotDatafiableReason } from '../../helpers';

export default function AutoScaleRenderer({
  volume,
  setSelectedVolumes,
  canWrite,
}: {
  volume: Volume;
  setSelectedVolumes: React.Dispatch<React.SetStateAction<string[]>>;
  canWrite: boolean;
}) {
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const isLoading = volume?.monitoring === 'Migrating' || volume?.monitoring === 'Unmigrating';
  const isExcluded = volume?.isDatafiable && isVolumeExcludedFromAutodatafy(volume);

  // Determine if volume is unattached or unmonitored
  const isUnattached = volume?.monitoring === 'Unattached';
  const isUnmonitored = volume?.monitoring === 'Unmonitored';
  // const isUnmanaged = volume?.monitoring === 'Unmanaged';
  const isToggleDisabled = !volume?.isDatafiable && !volume?.isUnDatafiable;
  const shouldShowWarning =
    hasError || isUnattached || isUnmonitored || isToggleDisabled || isExcluded;

  useEffect(() => {
    setHasError(false);
    setErrorMessage('');
  }, [volume?.id, volume?.volumeId]);

  // Handle errors from OptimisticSwitch
  const handleError = (message: string) => {
    setHasError(true);
    setErrorMessage(message);
  };

  const getWarningMessage = () => {
    if (hasError) {
      return errorMessage;
    }
    if (isExcluded) {
      return '';
    }

    // Prioritize API-provided reason over monitoring status
    if (isToggleDisabled && volume?.notDatafiableReason) {
      return formatNotDatafiableReason(volume.notDatafiableReason);
    }

    if (isUnattached) {
      return 'Unattached';
    }
    if (isUnmonitored) {
      return 'No AutoScaler';
    }

    // if (isUnmanaged) {
    //   return 'Unmanaged';
    // }
    return 'Unsupported (Other)';
  };

  return (
    <div className="flex gap-3.5 items-center w-full">
      <OptimisticSwitch
        switchSize="sm"
        volume={volume!}
        setSelectedVolumes={setSelectedVolumes}
        onError={handleError}
        canWrite={canWrite}
      />

      {!isLoading && shouldShowWarning && (
        <div className="flex gap-1 items-center">
          {hasError && <WarningIcon className={`size-5 text-red`} />}
          {isExcluded && <Exception className="size-5 text-gray-600 " />}
          {!hasError && !isExcluded && <InfoIcon className="size-5 text-gray-600" />}
          <Typography
            color="text-gray-600"
            variant="p3"
            fontWeight={500}
            className="font-family-inter"
          >
            {getWarningMessage()}
          </Typography>
        </div>
      )}

      {isLoading && !hasError && <LoadingProgress statusProgress={volume.statusProgress} />}
    </div>
  );
}

function LoadingProgress({ statusProgress }: { statusProgress?: number | string }) {
  const numeric =
    typeof statusProgress === 'number' || typeof statusProgress === 'string'
      ? Number(statusProgress)
      : undefined;
  const valid = numeric !== undefined && Number.isFinite(numeric) && numeric > 0;
  const clamped = valid ? Math.min(100, Math.max(0, Math.round(numeric!))) : undefined;

  const isQueued = numeric !== undefined && Number.isFinite(numeric) && numeric === 0;
  const isUndefined = numeric === undefined || !Number.isFinite(numeric);

  return (
    <div className="flex items-center w-fit">
      <span className="-ml-4.5 -mr-3 inline-block w-fit">
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData: loadingSpinner,
            rendererSettings: {
              preserveAspectRatio: 'xMidYMid meet',
            },
          }}
          height={28}
          width={56}
          isClickToPauseDisabled
        />
      </span>
      {valid && clamped !== undefined && clamped > 0 && (
        <Typography variant="p3" color="text-gray-600" className="">
          {`${clamped}%`}
        </Typography>
      )}
      {(isQueued || isUndefined) && (
        <Typography variant="p3" color="text-gray-600" className="">
          Queued
        </Typography>
      )}
    </div>
  );
}
